import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdDelete } from "react-icons/md";
import DataTable from "react-data-table-component";
import EfisModal from "../../../../components/UI/EfisModal/EfisModal";
import FormAlert from "../../../../components/UI/FormAlert/FormAlert";
import useApiClient, { apiRoutes } from "../../../../hooks/apiClient";
import { formatarData } from "../../../../components/Formatters/DateTime";
import FileDownloader from "../../../../components/UI/Button/FileDownloader";
import TextSpinner from "../../../../components/UI/Spinner/TextSpinner";
import constants from "../../../Constants";

const AnexosAlteracaoExecutor = ({ sqAlteracaoExecutor, sqAtividade, sqAndamento, permissao }) => {
  const [showAlerta, setShowAlerta] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [showExcluirConfirm, setShowExcluirConfirm] = useState({
    show: false,
    body: null,
    context: null,
  });

  const acoes = (newRow) => (
    <>
      <FileDownloader
        binario={newRow.conteudoBinario}
        tooltip={newRow.nomeArquivo !== null ? newRow.nomeArquivo : newRow.descricao}
        nomeArquivo={newRow.nomeArquivo}
        salvar
        className="ml-1"
      />
      {permissao ? (
        <OverlayTrigger placement="top" overlay={<Tooltip>{`Excluir ${newRow.nomeArquivo}?`}</Tooltip>}>
          <MdDelete
            onClick={() =>
              setShowExcluirConfirm({
                show: true,
                body: `Deseja realmente excluir arquivo: ${newRow.nomeArquivo}?`,
                context: newRow.sequencial,
              })
            }
            className="text-danger p-0 btn-lg btn-bg-transparent"
            title="Excluir"
          />
        </OverlayTrigger>
      ) : null}
    </>
  );

  const columns = [
    {
      name: "Descrição",
      cell: (row) => (row.descricao ? `${row.nomeArquivo} -> ${row.descricao}` : row.nomeArquivo),
    },
    {
      name: "Atividade Vinculada",
      cell: (row) => row.atividadeVinculada,
    },
    {
      name: "Inclusão",
      cell: (row) =>
        row.documentoAlteracao
          ? `${row.documentoAlteracao} - ${row.nmFormalAlteracao} - ${formatarData(
              row.tmAlteracao,
              "dd/MM/yyyy HH:mm:ss",
            )}`
          : `${row.documentoInclusao} - ${row.nmFormalInclusao} - ${formatarData(
              row.tmInclusao,
              "dd/MM/yyyy HH:mm:ss",
            )}`,
    },
    {
      name: "Ações",
      center: true,
      maxWidth: "1%",
      cell: (row) => acoes(row),
    },
  ];

  const { request: anexosRequest, data: anexosData, errors: anexosErrors, isLoading: anexosIsLoading } = useApiClient();

  useEffect(() => {
    if (sqAlteracaoExecutor && sqAtividade && sqAndamento) {
      anexosRequest(apiRoutes.fiscalizacao.alteracaoexecutor.anexosAtividade, {
        alteracaoExecutorOs: sqAlteracaoExecutor,
        sequencialAtividade: sqAtividade,
        sequencialAndamento: sqAndamento,
        pageSize,
        pageNumber,
      });
    }
  }, [anexosRequest, pageNumber, pageSize, sqAlteracaoExecutor, sqAndamento, sqAtividade]);

  useEffect(() => {
    if (anexosData && anexosData.result) {
      setAnexos(anexosData.result);
      setTotalRows(anexosData.result.length);
    }
    if (anexosErrors) {
      toast.error(anexosErrors.message);
    }
  }, [anexosData, anexosErrors]);

  const {
    request: deleteItemRequest,
    isLoading: deleteItemIsLoading,
    data: deleteItemData,
    errors: deleteItemErrors,
  } = useApiClient();

  const handlePageChange = useCallback(
    (newPageNumber) => {
      setPageNumber(newPageNumber);
      window.location.reload();
    },
    [setPageNumber],
  );

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  const refreshItems = useCallback(() => {
    handlePageChange();
  }, [handlePageChange]);

  useEffect(() => {
    if (!deleteItemData && !deleteItemErrors) return;

    if (deleteItemData) {
      toast.success("Item excluído com sucesso.");
      refreshItems();
    }

    if (deleteItemErrors) {
      setShowAlerta(true);
    }

    setShowExcluirConfirm({ show: false });
  }, [deleteItemData, deleteItemErrors, refreshItems, setShowExcluirConfirm]);

  return (
    <>
      <EfisModal
        show={showExcluirConfirm.show}
        body={showExcluirConfirm.body}
        loadingConfirmationAction={deleteItemIsLoading}
        closeHandler={() => setShowExcluirConfirm({ show: false })}
        confirmationHandler={() =>
          deleteItemRequest(apiRoutes.fiscalizacao.alteracaoexecutor.excluirAnexoAtividade, {
            sequencialAlteracaoExecutor: sqAlteracaoExecutor,
            sequencialAtividade: sqAtividade,
            sequencialAndamento: sqAndamento,
            sequencial: showExcluirConfirm.context,
          })
        }
      />
      <FormAlert
        show={showAlerta}
        errors={deleteItemErrors}
        isLoading={deleteItemIsLoading}
        closeHandler={() => setShowAlerta(false)}
      />
      {anexos.length > 0 ? (
        <DataTable
          noHeader
          keyField="sequencial"
          columns={columns}
          data={anexos}
          progressPending={anexosIsLoading}
          noDataComponent="Nenhum registro para exibir"
          progressComponent={<TextSpinner />}
          pagination
          paginationTotalRows={totalRows}
          pointerOnHover
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          paginationComponentOptions={constants.paginationComponentOptions}
          paginationPerPage={pageSize}
        />
      ) : null}
    </>
  );
};

export default AnexosAlteracaoExecutor;
